import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { User } from "@supabase/supabase-js";
import { CustomerTemplate } from "@/components/CustomerTemplateManager";
import type { ShippingInfo } from "@/components/ShippingInfoForm";
import * as parsedOrderService from "@/supabase/parsedorder/parsedOrderService";
import { downloadSettlementExcel, downloadFilteredOrdersExcel } from "@/utils/excelUtils";

// 배송정보 캐시 3상: get() === undefined = 미커버(unknown) · null = 없음 확정 · 객체 = 있음.
// 소비 지점은 truthiness가 아니라 3상 명시 판별을 쓸 것 (no-await-open 플랜 §A).
export type ShippingInfoCacheMap = Map<number, Partial<ShippingInfo> | null>;

const EMPTY_SHIPPING_INFO: ShippingInfoCacheMap = new Map();

// 배치 응답 병합 — 순수 함수 (vitest 단위 테스트 대상).
// - 배치 디스패치 이후의 로컬 쓰기(rev 비교)는 배치 결과(구식 양성·null 모두)보다 우선:
//   늦게 도착한 배치가 방금 저장한 값을 '입력' 상태로 되돌리지 못한다.
// - complete=false(청크 절단/실패)면 음성을 새로 만들지 않고 기존 엔트리(검증된 null 포함)를 보존.
// - 결과는 requestedIds로 한정 — 삭제된 주문의 엔트리는 부활하지 않는다.
export function mergeShippingInfoBatch(args: {
  prev: ShippingInfoCacheMap | null;
  batchPositives: Map<number, Partial<ShippingInfo>>;
  requestedIds: number[];
  complete: boolean;
  localWrites: Map<number, number>;
  revAtDispatch: number;
}): ShippingInfoCacheMap {
  const { prev, batchPositives, requestedIds, complete, localWrites, revAtDispatch } = args;
  const next: ShippingInfoCacheMap = new Map();
  for (const id of requestedIds) {
    const localRev = localWrites.get(id);
    if (localRev !== undefined && localRev > revAtDispatch && prev && prev.has(id)) {
      next.set(id, prev.get(id) ?? null);
    } else if (batchPositives.has(id)) {
      next.set(id, batchPositives.get(id)!);
    } else if (complete) {
      next.set(id, null);
    } else if (prev && prev.has(id)) {
      next.set(id, prev.get(id) ?? null);
    }
    // 그 외는 미기입(unknown 유지)
  }
  return next;
}

interface OrderSummaryItem {
  name: string;
  quantity: number;
  price: number;
  buyers: Record<string, number>;
}

interface UseOrderSummariesProps {
  customerTemplates: CustomerTemplate[];
  user: User | null;
  selectedDate: Date;
  statusFilter: string;
  keepFilter: string;
  sortOption: string;
}

interface UseOrderSummariesResult {
  ordersWithShippingInfo: Set<number>;
  shippingInfoByOrderId: ShippingInfoCacheMap;
  filteredCustomerTemplates: CustomerTemplate[];
  totalOrderSummary: OrderSummaryItem[];
  hasPaidOrders: boolean;
  handleDownloadSettlement: () => void;
  handleDownloadFilteredExcel: () => void;
  /**
   * 배송정보가 확정적으로 알려졌을 때(저장 성공 또는 모달 저장 게이트의 서버 발견) 캐시 갱신.
   * sessionUserId = 그 모달 세션을 소유한 유저 id — 현재 유저·현재 화면 주문이 아니면 완전 무시.
   */
  handleShippingInfoKnown: (orderId: number, shippingInfo: Partial<ShippingInfo>, sessionUserId: string) => void;
}

/**
 * 주문 요약·필터·정산 엑셀 도메인 훅.
 * 필터/정렬된 고객 목록(filteredCustomerTemplates) + 전체 주문 요약(totalOrderSummary) +
 * 입금 주문 존재 여부(hasPaidOrders) + 배송정보 캐시(ordersWithShippingInfo) + 정산/필터 엑셀 다운로드 핸들러를 응집.
 * customerTemplates 등 공유 hub는 Home 소유 → live 참조로 주입(snapshot 금지).
 */
export function useOrderSummaries({
  customerTemplates,
  user,
  selectedDate,
  statusFilter,
  keepFilter,
  sortOption,
}: UseOrderSummariesProps): UseOrderSummariesResult {
  const [shippingInfoCache, setShippingInfoCache] = useState<{
    userId: string;
    byOrderId: ShippingInfoCacheMap;
  } | null>(null);
  const shippingInfoByOrderId = shippingInfoCache && shippingInfoCache.userId === user?.id
    ? shippingInfoCache.byOrderId
    : EMPTY_SHIPPING_INFO;
  // 배지/버튼 라벨용 Set — 양성(non-null)만. null(없음 확정)은 포함하지 않는다.
  const ordersWithShippingInfo = useMemo(() => {
    const set = new Set<number>();
    shippingInfoByOrderId.forEach((value, key) => {
      if (value !== null) set.add(key);
    });
    return set;
  }, [shippingInfoByOrderId]);

  // 로컬 쓰기 rev 장부: 배치 디스패치 이후의 쓰기를 병합에서 보호한다. rev는 단조(리셋 금지).
  const revRef = useRef(0);
  const localWritesRef = useRef(new Map<number, number>());
  const userId = user?.id;
  // 비동기 완료 시점의 "현재 유저" 대조용 (배치 커밋·캐시 갱신 콜백의 명시 검증)
  const userIdRef = useRef(userId);
  userIdRef.current = userId;
  useEffect(() => {
    // 유저 전환 시 이전 유저의 쓰기 장부가 새 유저 병합에 끼어들지 않도록 클리어.
    localWritesRef.current = new Map();
  }, [userId]);

  // customerTemplates에서 order_id만 추출 → 정렬·중복제거된 안정 키로 메모이즈
  // 개별 주문의 status/keep/items 등 변경 시 재실행을 방지 (오더 ID 집합이 바뀔 때만 재조회)
  const orderIdsKey = useMemo(
    () =>
      Array.from(
        new Set(
          customerTemplates
            .map(c => {
              const match = c.id.match(/^order_(\d+)$/);
              return match ? Number(match[1]) : null;
            })
            .filter((id): id is number => id !== null),
        ),
      )
        .sort((a, b) => a - b)
        .join(','),
    [customerTemplates],
  );

  // orderIdsKey 변경 시, order_id 목록을 뽑아 배치 조회
  useEffect(() => {
    if (!user || orderIdsKey === '') {
      setShippingInfoCache(null);
      return;
    }

    let cancelled = false;
    const orderIds = orderIdsKey.split(',').map(Number);
    const capturedUserId = user.id;
    const revAtDispatch = revRef.current;

    (async () => {
      try {
        const { byOrderId: batchPositives, complete } =
          await parsedOrderService.getShippingInfoByOrderIds(orderIds);
        // 디스패치 시점 유저 === 현재 유저 명시 검증 (effect cleanup에만 의존하지 않는다)
        if (cancelled || capturedUserId !== userIdRef.current) return;
        setShippingInfoCache((prev) => ({
          userId: capturedUserId,
          byOrderId: mergeShippingInfoBatch({
            prev: prev && prev.userId === capturedUserId ? prev.byOrderId : null,
            batchPositives,
            requestedIds: orderIds,
            complete,
            localWrites: localWritesRef.current,
            revAtDispatch,
          }),
        }));
      } catch (e) {
        console.error('배송 정보 존재 여부 조회 실패:', e);
        if (!cancelled) setShippingInfoCache(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, orderIdsKey]);

  const filteredCustomerTemplates = useMemo(() => {
    let filtered = customerTemplates.filter(c => c.nickname !== '고객 닉네임');

    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => c.status === statusFilter);
    }

    if (keepFilter !== 'all') {
      const isKept = keepFilter === 'kept';
      filtered = filtered.filter(c => c.is_keep === isKept);
    }

    const groupedByNickname = filtered.reduce((acc, customer) => {
      const nickname = customer.nickname || '알 수 없는 고객';
      if (!acc[nickname]) {
        acc[nickname] = {
          customers: [],
          groupTotalPrice: 0,
          maxId: -1,
          minId: Infinity,
        };
      }
      acc[nickname].customers.push(customer);
      acc[nickname].groupTotalPrice += customer.totalPrice;

      const orderId = parseInt(customer.id.replace('order_', ''), 10);
      if (!isNaN(orderId)) {
        if (orderId > acc[nickname].maxId) {
          acc[nickname].maxId = orderId;
        }
        if (orderId < acc[nickname].minId) {
          acc[nickname].minId = orderId;
        }
      }
      return acc;
    }, {} as Record<string, { customers: CustomerTemplate[]; groupTotalPrice: number; maxId: number; minId: number }>);

    const sortedGroups = Object.values(groupedByNickname).sort((a, b) => {
      const compareByNickname = () => a.customers[0].nickname.localeCompare(b.customers[0].nickname);

      switch (sortOption) {
        case 'total-price-desc':
          const priceDiff = b.groupTotalPrice - a.groupTotalPrice;
          return priceDiff !== 0 ? priceDiff : compareByNickname();
        case 'id-asc':
          const ascIdDiff = a.minId - b.minId;
          return ascIdDiff !== 0 ? ascIdDiff : compareByNickname();
        case 'id-desc':
          const descIdDiff = b.maxId - a.maxId;
          return descIdDiff !== 0 ? descIdDiff : compareByNickname();
        case 'nickname-asc':
        default:
          return compareByNickname();
      }
    });

    return sortedGroups.flatMap(group => {
      const intraGroupSort = (a: CustomerTemplate, b: CustomerTemplate) => {
        const idA = parseInt(a.id.replace('order_', ''), 10) || 0;
        const idB = parseInt(b.id.replace('order_', ''), 10) || 0;

        // '최신순'일 때만 그룹 내부도 최신순으로 정렬, 그 외에는 모두 오래된 순으로 정렬
        if (sortOption === 'id-desc') {
          return idB - idA;
        }
        return idA - idB;
      };
      return group.customers.sort(intraGroupSort);
    });
  }, [customerTemplates, statusFilter, keepFilter, sortOption]);

  const totalOrderSummary = useMemo(() => {
    const summaryMap = new Map<string, { quantity: number; price: number; buyers: Record<string, number> }>();
    customerTemplates.filter(customer => customer.nickname !== '고객 닉네임').forEach(customer => {
      if (customer.status === '입금 완료' || customer.status === '출고 완료') {
        if (customer.items) {
          customer.items.forEach(item => {
            const existing = summaryMap.get(item.name);
            if (existing) {
              existing.quantity += item.quantity;
              existing.buyers[customer.nickname] = (existing.buyers[customer.nickname] || 0) + item.quantity;
            } else {
              summaryMap.set(item.name, {
                quantity: item.quantity,
                price: item.price,
                buyers: { [customer.nickname]: item.quantity },
              });
            }
          });
        }
      }
    });
    return Array.from(summaryMap, ([name, data]) => ({ name, quantity: data.quantity, price: data.price, buyers: data.buyers }))
      .sort((a, b) => b.quantity - a.quantity);
  }, [customerTemplates]);

  const hasPaidOrders = useMemo(() => {
    return customerTemplates.some(c => c.status === '입금 완료' && !c.is_keep);
  }, [customerTemplates]);

  const handleDownloadSettlement = useCallback(() => {
    downloadSettlementExcel(customerTemplates, selectedDate);
  }, [customerTemplates, selectedDate]);

  const handleDownloadFilteredExcel = useCallback(() => {
    downloadFilteredOrdersExcel(filteredCustomerTemplates, selectedDate);
  }, [filteredCustomerTemplates, selectedDate]);

  // 현재 화면의 주문 id 집합 — 캐시 갱신 콜백의 호출 시점 검증용
  const orderIdSet = useMemo(
    () => new Set(orderIdsKey === '' ? [] : orderIdsKey.split(',').map(Number)),
    [orderIdsKey],
  );

  const handleShippingInfoKnown = useCallback((orderId: number, shippingInfo: Partial<ShippingInfo>, sessionUserId: string) => {
    // 호출 시점 완전 검증: 세션 유저 ≠ 현재 유저이거나 현재 화면의 주문이 아니면
    // rev·장부·캐시 어느 것도 건드리지 않는다 (부분 부수효과 금지).
    const currentUserId = userIdRef.current;
    if (!currentUserId || sessionUserId !== currentUserId) return;
    if (!orderIdSet.has(orderId)) return;
    revRef.current += 1;
    localWritesRef.current.set(orderId, revRef.current);
    setShippingInfoCache((prev) => {
      // 다른 유저의 캐시를 대체하지 않는다(늦게 완료된 이전 유저 쓰기의 오염 방지).
      if (prev && prev.userId !== currentUserId) return prev;
      // 모든 캐시 커밋은 현재 orderIds로 필터(플랜 §A) — 날짜 전환 등으로 화면을 떠난
      // 주문의 엔트리가 로컬 커밋에 실려 살아남아 stale 양성 오픈을 만들지 못하게 한다.
      const byOrderId: ShippingInfoCacheMap = new Map();
      prev?.byOrderId.forEach((value, key) => {
        if (orderIdSet.has(key)) byOrderId.set(key, value);
      });
      byOrderId.set(orderId, shippingInfo);
      return { userId: currentUserId, byOrderId };
    });
  }, [orderIdSet]);

  return {
    ordersWithShippingInfo,
    shippingInfoByOrderId,
    filteredCustomerTemplates,
    totalOrderSummary,
    hasPaidOrders,
    handleDownloadSettlement,
    handleDownloadFilteredExcel,
    handleShippingInfoKnown,
  };
}
